// Tiny pub/sub queue for centered acknowledgement notifications.
// Replaces the toast/sonner corner notifications with modal dialogs that
// require an explicit OK click.

let nextId = 1;
const listeners = new Set();
let queue = []; // pending notifications, oldest first

function emit() {
  for (const l of listeners) l(queue);
}

export function subscribe(listener) {
  listeners.add(listener);
  listener(queue);
  return () => listeners.delete(listener);
}

// Coerce anything the caller passes into a human-readable string.
// FastAPI 422 validation errors are `{ detail: [{loc, msg, type}, ...] }`,
// so `err.response.data.detail` is an array of objects — Array#toString on
// that gives "[object Object],[object Object]" which is what surfaced as
// "Error [object Object]" in the modal. Handle these shapes explicitly.
function _stringifyMessage(msg) {
  if (msg === null || msg === undefined) return "";
  if (typeof msg === "string") return msg;
  if (msg instanceof Error) return msg.message || String(msg);
  if (Array.isArray(msg)) {
    const parts = msg
      .map((m) => (m && typeof m === "object" ? (m.msg || m.detail || m.message || JSON.stringify(m)) : String(m)))
      .filter(Boolean);
    return parts.join("; ");
  }
  if (typeof msg === "object") {
    // FastAPI single-item shape (already unwrapped): {msg, loc, type}
    if (msg.msg) return String(msg.msg);
    if (msg.detail) return _stringifyMessage(msg.detail); // nested {detail: [...]}
    if (msg.message) return String(msg.message);
    try { return JSON.stringify(msg); } catch { return String(msg); }
  }
  return String(msg);
}

export function pushNotification({ type = "info", message, description = "" }) {
  const text = _stringifyMessage(message);
  if (!text.trim() && !description) return;
  // Collapse identical back-to-back duplicates so a stray double-call doesn't
  // force the user to click OK twice for the exact same message.
  const last = queue[queue.length - 1];
  if (last && last.type === type && last.message === text && last.description === description) return;
  queue = [...queue, { id: nextId++, type, message: text, description }];
  emit();
}

export function dismissCurrent() {
  if (queue.length === 0) return;
  queue = queue.slice(1);
  emit();
}

export function dismissAll() {
  if (queue.length === 0) return;
  queue = [];
  emit();
}
